import {
  Checkbox,
  Dialog,
  DialogMaxWidth,
  DialogVariant,
  LinkButton,
  SearchBarInput,
  Text,
  TextSize
} from '@focaldata/cin-ui-components'
import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import useStyles from './MarketVisibilityDialog.styles'
import { useProjectMarkets } from './useProjectMarkets'

interface Props {
  open: boolean
  /** Label of the thing being scoped (question / option / row text). */
  targetLabel: string
  /** Survey ids where the item is currently hidden. */
  initialHiddenInMarkets: string[]
  /**
   * Markets where the *parent* is hidden (the question an option/row belongs
   * to). They are removed from the list, since a response option or matrix row
   * can never be visible where its question is not. Omit/empty for entry-level
   * visibility, where every project market applies.
   */
  parentHiddenInMarkets?: string[]
  saving: boolean
  onClose: () => void
  onSave: (hiddenInMarkets: string[]) => void
}

/**
 * Market-visibility editor. Lists the applicable markets (the base market first,
 * then the rest alphabetical) as checkboxes — checked = visible, unchecked =
 * hidden. The base (master) market is a market like any other here. We track the
 * *hidden* set, so any market not explicitly hidden is visible by default
 * (including markets added after the dialog data first loaded). On save we send
 * that hidden set as `hiddenInMarkets` (replace semantics); markets the dialog
 * doesn't show — e.g. where the parent is hidden — keep their state untouched.
 * A search box filters the list; Select/Unselect all act on the current filter.
 */
const MarketVisibilityDialog: FC<Props> = ({
  open,
  targetLabel,
  initialHiddenInMarkets,
  parentHiddenInMarkets = [],
  saving,
  onClose,
  onSave
}) => {
  const { classes } = useStyles()
  const allMarkets = useProjectMarkets()

  // A sub-item can only live in the markets where its parent is visible.
  const markets = useMemo(() => {
    const parentHidden = new Set(parentHiddenInMarkets)
    return allMarkets.filter((m) => !parentHidden.has(m.surveyId))
  }, [allMarkets, parentHiddenInMarkets])

  const [hiddenMarketIds, setHiddenMarketIds] = useState<Set<string>>(new Set())
  const [query, setQuery] = useState('')

  // Seed the hidden set once per open (guarded so a parent re-render — or a
  // fresh `initialHiddenInMarkets` array — doesn't wipe in-progress toggles).
  const initialisedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      initialisedRef.current = false
      setQuery('')
      return
    }
    if (initialisedRef.current) {
      return
    }
    setHiddenMarketIds(new Set(initialHiddenInMarkets))
    initialisedRef.current = true
  }, [open, initialHiddenInMarkets])

  const displayed = useMemo(() => {
    const needle = query.trim().toLowerCase()
    if (!needle) {
      return markets
    }
    return markets.filter((m) => m.label.toLowerCase().includes(needle))
  }, [markets, query])

  const toggleMarket = (surveyId: string) => {
    setHiddenMarketIds((prev) => {
      const next = new Set(prev)
      if (next.has(surveyId)) {
        next.delete(surveyId)
      } else {
        next.add(surveyId)
      }
      return next
    })
  }

  // Select/Unselect all operate on the markets currently shown (the filter).
  const selectAll = () =>
    setHiddenMarketIds((prev) => {
      const next = new Set(prev)
      for (const m of displayed) {
        next.delete(m.surveyId)
      }
      return next
    })
  const unselectAll = () =>
    setHiddenMarketIds((prev) => {
      const next = new Set(prev)
      for (const m of displayed) {
        next.add(m.surveyId)
      }
      return next
    })

  // The hidden set already preserves entries the dialog never showed (e.g.
  // markets where the parent is hidden), so it is the value to save as-is.
  const handleSave = () => onSave([...hiddenMarketIds])

  const selectedCount = markets.filter(
    (m) => !hiddenMarketIds.has(m.surveyId)
  ).length

  return (
    <Dialog
      title={`Set market visibility for “${targetLabel}”`}
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={DialogMaxWidth.Md}
      variant={DialogVariant.DoubleButton}
      secondaryButtonText="Cancel"
      secondaryButtonClick={onClose}
      primaryButtonText="Save Changes"
      primaryButtonClick={handleSave}
      primaryButtonLoading={saving}
    >
      <Text size={TextSize.ms}>
        Choose which markets this is visible in. Deselect any that don’t apply —
        it stays visible in every applicable market by default.
      </Text>

      {markets.length > 8 && (
        <div className={classes.search}>
          <SearchBarInput
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onClickClear={() => setQuery('')}
            showClearIcon={query.length > 0}
            placeholder="Search markets"
          />
        </div>
      )}

      <div className={classes.toolbar}>
        <LinkButton onClick={selectAll} noPadding>
          Select all
        </LinkButton>
        <LinkButton onClick={unselectAll} noPadding>
          Unselect all
        </LinkButton>
        <span className={classes.count}>
          <Text size={TextSize.ms}>
            {`${selectedCount} of ${markets.length} markets selected`}
          </Text>
        </span>
      </div>

      <div className={classes.list}>
        {displayed.length === 0 ? (
          query ? (
            <Text size={TextSize.ms}>No markets match “{query}”.</Text>
          ) : null
        ) : (
          <div className={classes.columns}>
            {displayed.map((market) => (
              <div key={market.surveyId} className={classes.row}>
                <Checkbox
                  checked={!hiddenMarketIds.has(market.surveyId)}
                  onChange={() => toggleMarket(market.surveyId)}
                  textSize={TextSize.ms}
                  label={
                    <span className={classes.rowLabel}>
                      {market.flag && (
                        <span className={classes.flag}>{market.flag}</span>
                      )}
                      {market.label}
                    </span>
                  }
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  )
}

export default MarketVisibilityDialog
